import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import { FaFileExcel, FaSearch, FaEraser } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';
import { createDate, salvarEmExcel } from '~/Utils/Helpers';
import * as constants from '~/constant';

// Fonte: ERP/ModuloImob/URelBaixa.pas + UQRBaixa.pas. Relatório read-only (GET apenas).
// Origem do pagamento — espelha RadioButton rc/rb/rt do Delphi.
const ORIGEM_CAIXA = 0;
const ORIGEM_BANCO = 1;
const ORIGEM_TODAS = 2;

const primeiroDiaMesAtual = () => {
  const now = new Date();
  return `01/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
};

const hoje = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
};

const formatarValor = valor => {
  const numero = typeof valor === 'number' ? valor : Number(valor) || 0;
  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatarData = valor => {
  if (!valor) return '';
  const d = createDate(valor);
  return d && d.isValid() ? d.format(constants.DATE_DEFAULT_FORMAT) : '';
};

const formatarParcela = item => {
  const parcela = item.parcela != null ? String(item.parcela).padStart(3, '0') : '000';
  const plano = item.plano != null ? String(item.plano).padStart(3, '0') : '000';
  return `${parcela}/${plano}`;
};

export default function BaixaTitulo() {
  const [familias, setFamilias] = useState([]);
  const [razoes, setRazoes] = useState([]);
  const [familiaSelecionada, setFamiliaSelecionada] = useState(null);
  const [razaoSelecionada, setRazaoSelecionada] = useState(null);

  const [dataInicial, setDataInicial] = useState(primeiroDiaMesAtual());
  const [dataFinal, setDataFinal] = useState(hoje());
  const [origemPagamento, setOrigemPagamento] = useState(ORIGEM_CAIXA);
  const [intVendas, setIntVendas] = useState(false);

  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Carregar combos (Empreendimento + Razão Social).
  useEffect(() => {
    api
      .buscarBaixaTituloFamilias()
      .then(resp => {
        const lista = Array.isArray(resp) ? resp : [];
        setFamilias(lista.map(f => ({ value: f.codigo_familia, label: f.nome_da_familia })));
      })
      .catch(() => toast.error('Erro ao carregar Empreendimentos.'));

    api
      .buscarBaixaTituloRazoesSociais()
      .then(resp => {
        const lista = Array.isArray(resp) ? resp : [];
        setRazoes(lista.map(r => ({ value: r.codigo, label: r.razao })));
      })
      .catch(() => toast.error('Erro ao carregar Razões Sociais.'));
  }, []);

  const toServerDate = useCallback(str => {
    if (!str) return null;
    const d = createDate(str, constants.DATE_DEFAULT_FORMAT);
    return d.isValid() ? d.format(constants.DATE_FORMAT_SERVER) : null;
  }, []);

  // RN-02 (Empreendimento OU Razão) + RN-03 (período) — gate do botão Gerar.
  const obrigatoriosOk = useMemo(() => {
    const temFiltro = familiaSelecionada != null || razaoSelecionada != null;
    const temPeriodo = !!dataInicial && !!dataFinal;
    return temFiltro && temPeriodo;
  }, [familiaSelecionada, razaoSelecionada, dataInicial, dataFinal]);

  const validar = useCallback(() => {
    if (familiaSelecionada == null && razaoSelecionada == null) {
      toast.error('Razão Social ou Empreendimento não pode ser nulo.');
      return false;
    }
    if (!dataInicial || !dataFinal) {
      toast.error('Período incorreto.');
      return false;
    }
    const di = createDate(dataInicial, constants.DATE_DEFAULT_FORMAT);
    const df = createDate(dataFinal, constants.DATE_DEFAULT_FORMAT);
    if (!di.isValid() || !df.isValid() || df.isBefore(di)) {
      toast.error('Período incorreto.');
      return false;
    }
    return true;
  }, [familiaSelecionada, razaoSelecionada, dataInicial, dataFinal]);

  const montarParams = useCallback(
    () => ({
      codigoFamilia: familiaSelecionada != null ? familiaSelecionada.value : null,
      codigoRazao: razaoSelecionada != null ? razaoSelecionada.value : null,
      dataInicial: toServerDate(dataInicial),
      dataFinal: toServerDate(dataFinal),
      origemPagamento,
      intVendas,
    }),
    [
      familiaSelecionada,
      razaoSelecionada,
      toServerDate,
      dataInicial,
      dataFinal,
      origemPagamento,
      intVendas,
    ]
  );

  const gerar = async () => {
    if (!validar()) return;
    setLoading(true);
    setDados(null);
    try {
      const resp = await api.buscarBaixaTitulo(montarParams());
      const lista = Array.isArray(resp) ? resp : [];
      setDados(lista);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro inesperado');
    } finally {
      setLoading(false);
    }
  };

  const limpar = () => {
    setFamiliaSelecionada(null);
    setRazaoSelecionada(null);
    setDataInicial(primeiroDiaMesAtual());
    setDataFinal(hoje());
    setOrigemPagamento(ORIGEM_CAIXA);
    setIntVendas(false);
    setDados(null);
  };

  // Exibe colunas decompostas de Sinal Urbano quando há ao menos uma linha com incorporação.
  const temSinalUrbano = useMemo(() => {
    if (!dados) return false;
    return dados.some(item => item.titulo_imob != null && item.titulo_imob !== 0);
  }, [dados]);

  // Agrupamento Empreendimento (codigo_familia) → Tipo (Banco/Interna) — RN-11.
  const grupos = useMemo(() => {
    if (!dados) return [];
    const mapaFamilia = new Map();
    dados.forEach(item => {
      const chaveFamilia = item.codigo_familia != null ? item.codigo_familia : 0;
      if (!mapaFamilia.has(chaveFamilia)) {
        mapaFamilia.set(chaveFamilia, {
          codigoFamilia: chaveFamilia,
          nomeFamilia: item.nome,
          tipos: new Map(),
        });
      }
      const grupoFamilia = mapaFamilia.get(chaveFamilia);
      const chaveTipo = item.tipo || 'Interna';
      if (!grupoFamilia.tipos.has(chaveTipo)) {
        grupoFamilia.tipos.set(chaveTipo, []);
      }
      grupoFamilia.tipos.get(chaveTipo).push(item);
    });
    return Array.from(mapaFamilia.values()).map(gf => ({
      codigoFamilia: gf.codigoFamilia,
      nomeFamilia: gf.nomeFamilia,
      tipos: Array.from(gf.tipos.entries()).map(([tipo, itens]) => ({ tipo, itens })),
    }));
  }, [dados]);

  const totalGeral = useMemo(() => {
    if (!dados) return { valor: 0, juros: 0 };
    return dados.reduce(
      (acc, item) => ({
        valor: acc.valor + (Number(item.valor) || 0),
        juros: acc.juros + (Number(item.juros) || 0),
      }),
      { valor: 0, juros: 0 }
    );
  }, [dados]);

  const totalColunas = useMemo(() => (temSinalUrbano ? 17 : 13), [temSinalUrbano]);

  const exportarExcel = async () => {
    if (!dados || dados.length === 0) return;
    setExportando(true);
    try {
      const linhas = dados.map(item => {
        const linha = {
          'N° Título': item.codigo_titulo,
          Parc: formatarParcela(item),
          Imóvel: `${item.codigo_ativo} (${item.mapa1 || ''}/${item.mapa2 || ''})`,
          Cliente: `${item.codigo_conta != null ? item.codigo_conta : ''} - ${item.razao_social || ''}`,
          Vencto: formatarData(item.data_vencimento),
          DataBX: formatarData(item.data_baixa),
          Valor: formatarValor(item.valor),
          Juros: formatarValor(item.juros),
          CtaBX: item.conta_portador_bx,
          Cta: item.conta_portador,
          'Dt.Contábil': formatarData(item.data_contabil),
          P: item.tipo_financiamento,
        };
        if (temSinalUrbano) {
          linha['Sinal Urba'] = formatarValor(item.valor_sinal_urb);
          linha['Juros Urba'] = formatarValor(item.valor_juros_urb);
          linha['Liq Comp.'] = formatarValor(item.valor_liq_comp);
          linha['Juros Comp.'] = formatarValor(item.valor_juros_comp);
        }
        return linha;
      });
      await salvarEmExcel(linhas, 'baixa-de-titulo');
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro inesperado');
    } finally {
      setExportando(false);
    }
  };

  const possuiDados = dados && dados.length > 0;

  const renderLinha = item => (
    <tr key={`${item.codigo_titulo}-${item.codigo_ativo}-${item.tipo_bx != null ? item.tipo_bx : 'x'}`}>
      <td>{item.codigo_titulo}</td>
      <td>{formatarParcela(item)}</td>
      <td>{`${item.codigo_ativo} (${item.mapa1 || ''}/${item.mapa2 || ''})`}</td>
      <td>{`${item.codigo_conta != null ? item.codigo_conta : ''} - ${item.razao_social || ''}`}</td>
      <td>{formatarData(item.data_vencimento)}</td>
      <td>{formatarData(item.data_baixa)}</td>
      <td className="text-right">{formatarValor(item.valor)}</td>
      <td className="text-right">{formatarValor(item.juros)}</td>
      <td>{item.conta_portador_bx}</td>
      <td>{item.conta_portador}</td>
      <td>{formatarData(item.data_contabil)}</td>
      <td>{item.tipo_financiamento}</td>
      {temSinalUrbano && (
        <>
          <td className="text-right">{formatarValor(item.valor_sinal_urb)}</td>
          <td className="text-right">{formatarValor(item.valor_juros_urb)}</td>
          <td className="text-right">{formatarValor(item.valor_liq_comp)}</td>
          <td className="text-right">{formatarValor(item.valor_juros_comp)}</td>
        </>
      )}
    </tr>
  );

  return (
    <Container fluid className="mt-3">
      <Header title="Baixa de Título" />

      <Card className="mt-3 mb-3">
        <Card.Body>
          <Row className="mb-2">
            <Col md={4}>
              <Form.Label className="font-weight-bold mb-1">Empreendimento</Form.Label>
              <Select
                isClearable
                placeholder="Selecione"
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={familias}
                value={familiaSelecionada}
                onChange={opt => setFamiliaSelecionada(opt)}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="font-weight-bold mb-1">Razão Social</Form.Label>
              <Select
                isClearable
                placeholder="Selecione"
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={razoes}
                value={razaoSelecionada}
                onChange={opt => setRazaoSelecionada(opt)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="font-weight-bold mb-1">Data Inicial</Form.Label>
              <MaskedInput
                mask={constants.MASK_DATE}
                className="form-control"
                style={{ fontSize: '0.875rem', height: '31px', padding: '2px 8px' }}
                placeholder="dd/mm/aaaa"
                value={dataInicial}
                onChange={e => setDataInicial(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="font-weight-bold mb-1">Data Final</Form.Label>
              <MaskedInput
                mask={constants.MASK_DATE}
                className="form-control"
                style={{ fontSize: '0.875rem', height: '31px', padding: '2px 8px' }}
                placeholder="dd/mm/aaaa"
                value={dataFinal}
                onChange={e => setDataFinal(e.target.value)}
              />
            </Col>
          </Row>

          <Row className="align-items-end">
            <Col md={5}>
              <Form.Label className="font-weight-bold mb-1 d-block">Origem do pagamento</Form.Label>
              <Form.Check
                inline
                type="radio"
                id="radio-origem-caixa"
                name="origemPagamento"
                label="Caixa"
                checked={origemPagamento === ORIGEM_CAIXA}
                onChange={() => setOrigemPagamento(ORIGEM_CAIXA)}
              />
              <Form.Check
                inline
                type="radio"
                id="radio-origem-banco"
                name="origemPagamento"
                label="Banco/Bradesco"
                checked={origemPagamento === ORIGEM_BANCO}
                onChange={() => setOrigemPagamento(ORIGEM_BANCO)}
              />
              <Form.Check
                inline
                type="radio"
                id="radio-origem-todas"
                name="origemPagamento"
                label="Todas"
                checked={origemPagamento === ORIGEM_TODAS}
                onChange={() => setOrigemPagamento(ORIGEM_TODAS)}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                type="checkbox"
                id="check-int-vendas"
                label="Int. Vendas"
                checked={intVendas}
                onChange={e => setIntVendas(e.target.checked)}
              />
            </Col>
            <Col md={4} className="text-right">
              <Button variant="secondary" className="mr-2" onClick={limpar} disabled={loading}>
                <FaEraser className="mr-1" /> Limpar
              </Button>
              <Button variant="primary" onClick={gerar} disabled={loading || !obrigatoriosOk}>
                <FaSearch className="mr-1" /> Gerar
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <small className="text-muted">* Filtro pela Data Contábil</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="success"
          size="sm"
          onClick={exportarExcel}
          disabled={!possuiDados || exportando}
        >
          {exportando ? (
            <Spinner animation="border" size="sm" className="mr-1" />
          ) : (
            <FaFileExcel className="mr-1" />
          )}
          Exportar Excel
        </Button>
      </div>

      <Card>
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          Resultado {possuiDados ? `(${dados.length} títulos)` : ''}
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover size="sm" responsive className="mb-0">
              <thead>
                <tr>
                  <th>N° Título</th>
                  <th>Parc</th>
                  <th>Imóvel</th>
                  <th>Cliente</th>
                  <th>Vencto</th>
                  <th>DataBX</th>
                  <th className="text-right">Valor</th>
                  <th className="text-right">Juros</th>
                  <th>CtaBX</th>
                  <th>Cta</th>
                  <th>Dt.Contábil</th>
                  <th>P</th>
                  {temSinalUrbano && (
                    <>
                      <th className="text-right">Sinal Urba</th>
                      <th className="text-right">Juros Urba</th>
                      <th className="text-right">Liq Comp.</th>
                      <th className="text-right">Juros Comp.</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {possuiDados ? (
                  grupos.map(grupoFamilia => (
                    <React.Fragment key={`fam-${grupoFamilia.codigoFamilia}`}>
                      <tr style={{ backgroundColor: '#dde6f5', fontWeight: 'bold' }}>
                        <td colSpan={totalColunas}>
                          {`${grupoFamilia.codigoFamilia} - ${grupoFamilia.nomeFamilia || ''}`}
                        </td>
                      </tr>
                      {grupoFamilia.tipos.map(grupoTipo => (
                        <React.Fragment
                          key={`fam-${grupoFamilia.codigoFamilia}-tipo-${grupoTipo.tipo}`}
                        >
                          <tr style={{ backgroundColor: '#eef2f9' }}>
                            <td colSpan={totalColunas}>
                              <em>{grupoTipo.tipo}</em>
                            </td>
                          </tr>
                          {grupoTipo.itens.map(renderLinha)}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={totalColunas} className="text-center text-muted p-3">
                      Nenhum título encontrado.
                    </td>
                  </tr>
                )}
                {possuiDados && (
                  <tr style={{ fontWeight: 'bold', backgroundColor: '#d4edda' }}>
                    <td colSpan={6} className="text-right">
                      Total Geral
                    </td>
                    <td className="text-right">{formatarValor(totalGeral.valor)}</td>
                    <td className="text-right">{formatarValor(totalGeral.juros)}</td>
                    <td colSpan={temSinalUrbano ? 9 : 5} />
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
